import { ref, nextTick } from "vue";

export type MorphAnimationOptions = {
  duration?: number;
  easing?: string;
  scaleEffect?: boolean;
  fadeEffect?: boolean;
};

const DEFAULT_OPTIONS: MorphAnimationOptions = {
  duration: 400,
  easing: "cubic-bezier(0.4, 0.0, 0.2, 1)",
  scaleEffect: true,
  fadeEffect: true
};

export function useMorphAnimation() {
  const isAnimating = ref(false);

  const createMorphElement = (
    sourceElement: HTMLElement
  ): HTMLElement => {
    const morphElement = sourceElement.cloneNode(true) as HTMLElement;
    const sourceRect = sourceElement.getBoundingClientRect();

    // Style the morph element to match the source position and size
    morphElement.style.position = "fixed";
    morphElement.style.top = `${sourceRect.top}px`;
    morphElement.style.left = `${sourceRect.left}px`;
    morphElement.style.width = `${sourceRect.width}px`;
    morphElement.style.height = `${sourceRect.height}px`;
    morphElement.style.zIndex = "9999";
    morphElement.style.pointerEvents = "none";
    morphElement.style.transition = "none";
    morphElement.style.transformOrigin = "center center";

    // Add a subtle glow effect to make it more visible
    morphElement.style.boxShadow = "0 8px 32px rgba(102, 126, 234, 0.3)";
    morphElement.style.borderRadius = "8px";

    // Add the morph element to the body
    document.body.appendChild(morphElement);

    return morphElement;
  };

  const morphToModal = async (
    sourceElement: HTMLElement,
    modalContainer: HTMLElement,
    options: MorphAnimationOptions = {}
  ): Promise<void> => {
    if (isAnimating.value) return;

    isAnimating.value = true;
    const opts = { ...DEFAULT_OPTIONS, ...options };

    try {
      // Wait for the modal to be rendered
      await nextTick();

      // Add a highlight effect to the source element
      const originalTransition = sourceElement.style.transition;
      const originalBoxShadow = sourceElement.style.boxShadow;
      const originalTransform = sourceElement.style.transform;

      sourceElement.style.transition = "all 0.15s ease";
      sourceElement.style.boxShadow =
        "0 0 0 2px rgba(102, 126, 234, 0.5), 0 4px 20px rgba(102, 126, 234, 0.3)";
      sourceElement.style.transform = "scale(1.02)";

      // Wait a bit longer for the highlight effect to be seen
      await new Promise((resolve) => setTimeout(resolve, 80));

      const morphElement = createMorphElement(sourceElement);
      const modalRect = modalContainer.getBoundingClientRect();

      // Reset source element styles faster
      setTimeout(() => {
        sourceElement.style.transition = originalTransition;
        sourceElement.style.boxShadow = originalBoxShadow;
        sourceElement.style.transform = originalTransform;
      }, 30);

      // Set up the transition
      morphElement.style.transition = `all ${opts.duration}ms ${opts.easing}`;

      // Don't apply effects to the modal container - let the modal component handle its own visibility
      // The morph element will handle the visual transition

      // Force a reflow to ensure initial styles are applied
      morphElement.offsetHeight;

      // Start the animation with a single fluid transition
      requestAnimationFrame(() => {
        // Animate the morph element to the modal position and size in one go
        morphElement.style.top = `${modalRect.top}px`;
        morphElement.style.left = `${modalRect.left}px`;
        morphElement.style.width = `${modalRect.width}px`;
        morphElement.style.height = `${modalRect.height}px`;

        // Single fluid scaling with bounce built into the easing
        if (opts.scaleEffect) {
          morphElement.style.transform = "scale(1.02)"; // Slight overshoot for natural feel
        }

        // Enhanced visual effects that fade during animation
        morphElement.style.boxShadow = "0 20px 60px rgba(102, 126, 234, 0.3)";
        morphElement.style.borderRadius = "12px";

        if (opts.fadeEffect) {
          morphElement.style.opacity = "0.1"; // Fade out as it reaches destination
        }
      });

      // No secondary animation - keep it as one fluid motion

      // Wait for animation to complete
      await new Promise<void>((resolve) => {
        setTimeout(() => {
          // Clean up the morph element
          if (morphElement.parentNode) {
            morphElement.parentNode.removeChild(morphElement);
          }

          resolve();
        }, opts.duration || 400);
      });
    } finally {
      isAnimating.value = false;
    }
  };

  const morphFromModal = async (
    modalContainer: HTMLElement,
    targetElement: HTMLElement,
    options: MorphAnimationOptions = {}
  ): Promise<void> => {
    if (isAnimating.value) return;

    isAnimating.value = true;
    const opts = { ...DEFAULT_OPTIONS, ...options };

    try {
      // Create a clone of the modal at its current position
      const morphElement = createMorphElement(modalContainer);
      const targetRect = targetElement.getBoundingClientRect();

      // Set up the transition
      morphElement.style.transition = `all ${opts.duration}ms ${opts.easing}`;

      // Start with the modal fully visible
      morphElement.style.opacity = "1";
      morphElement.style.transform = "scale(1)";

      // Force a reflow
      morphElement.offsetHeight;

      // Start the reverse animation
      requestAnimationFrame(() => {
        morphElement.style.top = `${targetRect.top}px`;
        morphElement.style.left = `${targetRect.left}px`;
        morphElement.style.width = `${targetRect.width}px`;
        morphElement.style.height = `${targetRect.height}px`;

        if (opts.fadeEffect) {
          morphElement.style.opacity = "0.8";
        }

        if (opts.scaleEffect) {
          morphElement.style.transform = "scale(0.9)";
        }
      });

      // Wait for animation to complete
      await new Promise<void>((resolve) => {
        setTimeout(() => {
          // Clean up
          if (morphElement.parentNode) {
            morphElement.parentNode.removeChild(morphElement);
          }
          resolve();
        }, opts.duration || 400);
      });
    } finally {
      isAnimating.value = false;
    }
  };

  const resetModalStyles = (modalContainer: HTMLElement | null) => {
    if (!modalContainer || !modalContainer.style) return;

    modalContainer.style.opacity = "";
    modalContainer.style.transform = "";
    modalContainer.style.transition = "";
    modalContainer.classList.remove("morphing-close");
  };

  return {
    isAnimating,
    morphToModal,
    morphFromModal,
    resetModalStyles
  };
}
